/**
 * Pre-reading dossier modal — opened from the `BriefPanel` "Pre-reading"
 * row. Renders per-workshop dossier content sourced from
 * `doc-content.ts` (WS4 ports the full vanilla copy verbatim; other
 * workshops fall back to a same-shape placeholder).
 *
 * Ported from:
 *   - `_legacy/wd.html` lines 112-134 — `#docModal` shell. The legacy
 *     chrome is reproduced verbatim: `Eye` icon + "PRE-READING DOSSIER"
 *     title + "DOC N OF M" blue pill, with the close button alone on
 *     the right (no `flex-row-6` status cluster like Map/Report modals).
 *     Footer is the canonical "XAES Phase Zero | <workshop longTitle>"
 *     pair, no third segment.
 *   - `_legacy/ws4_live_content.js` lines 143-186 — `WS4_PREREADING.data`
 *     section structure.
 *   - `_legacy/ws4-modals.js` lines 33-67 — `renderDocModal()` typography
 *     for body paragraphs (Inter, `var(--fs-base)`, line-height 1.7,
 *     `text-secondary`). Section headings use prose styling (sentence-case
 *     Inter 700 `var(--fs-title)`, `text-primary`, no uppercase, no
 *     letter-spacing) so they read as document subheadings rather than
 *     modal eyebrow chrome. A subtle true-blue separator divides
 *     sections so they don't run together. Callout sections (the legacy
 *     `s.callout === true` flag, now `kind: "callout"`) retain the
 *     tinted left-bordered block — heading kept as an uppercase eyebrow
 *     because the block is visually distinct from prose flow.
 *
 * Chrome contract:
 *   - Uses `PanelDialogContent` + the legacy `.modal-shell` markup so
 *     this briefing modal matches the other Wave-4 modals
 *     (AnalysisModal, ReportModal, AgendaModal).
 *   - Body uses an inner scroll container so long dossiers scroll
 *     inside the shell rather than blowing out the modal height.
 *   - Width/height constraints match legacy verbatim:
 *     `width:72vw; max-width:820px; max-height:calc(100vh - 56px - 112px - 24px)`.
 */

import { useEffect, useState } from "react";
import { Download, Eye, FileText, Loader2, X } from "lucide-react";
import { workshopLabelColor } from "@/components/tags/WorkshopLabelPill";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { DOC_BY_WORKSHOP, type DocSection } from "./doc-content";
import {
  isPdfUpload,
  resolveDocUpload,
  resolveField,
  type DocUpload,
} from "@/features/workshop/overrides/workshop-override-content";
import { useEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { downloadDoc } from "@/lib/docs-api";

type DocModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: Workshop["id"];
};

/** Shared prose-paragraph type. */
const PARAGRAPH_STYLE: React.CSSProperties = {
  fontFamily: "Inter, sans-serif",
  fontSize: "var(--fs-base)",
  lineHeight: 1.7,
  color: "var(--text-secondary)",
  marginBottom: 14,
};

/** Bullet-list type — same body type as paragraphs, native disc bullets. */
const LIST_STYLE: React.CSSProperties = {
  fontFamily: "Inter, sans-serif",
  fontSize: "var(--fs-base)",
  lineHeight: 1.7,
  color: "var(--text-secondary)",
  listStyleType: "disc",
  paddingLeft: 20,
  marginTop: 0,
  marginBottom: 14,
};

/** Strip a leading "• " or "- " bullet marker from a paragraph string. */
function stripBulletMarker(s: string): string {
  return s.replace(/^\s*(?:[••]|-)\s+/, "");
}

/** Detect a paragraph whose author intended it as a bullet (legacy
 *  `• `/`- ` prefix). Used by the auto-promote path below. */
function isBulletLine(s: string): boolean {
  return /^\s*(?:[••]|-)\s+/.test(s);
}

/**
 * Render the prose body of a section: paragraphs and/or a bullet list.
 *
 * Behaviour:
 *   - `body: string` → single `<p>`.
 *   - `body: string[]` → consecutive non-bullet lines render as `<p>`;
 *     runs of bullet-prefixed (`• `/`- `) lines collapse into a single
 *     `<ul>` with the markers stripped. This preserves the legacy
 *     mixed intro+items shape when a section was authored as a flat
 *     array rather than the explicit `intro` + `items` fields.
 *   - `intro` + `items` → intro `<p>` followed by a `<ul>` with proper
 *     disc bullets (the structured legacy shape).
 */
function SectionProse({
  body,
  intro,
  items,
}: {
  body?: DocSection["body"];
  intro?: string;
  items?: string[];
}) {
  // Flatten `body` into an array of paragraphs for uniform handling.
  const bodyParas =
    body === undefined ? [] : Array.isArray(body) ? body : [body];

  // Group consecutive bullet lines so a flat array like
  // ["intro", "• one", "• two"] renders as <p>intro</p><ul>…</ul>.
  type Block =
    | { type: "p"; text: string }
    | { type: "ul"; items: string[] };
  const blocks: Block[] = [];
  for (const para of bodyParas) {
    if (isBulletLine(para)) {
      const last = blocks[blocks.length - 1];
      if (last && last.type === "ul") {
        last.items.push(stripBulletMarker(para));
      } else {
        blocks.push({ type: "ul", items: [stripBulletMarker(para)] });
      }
    } else {
      blocks.push({ type: "p", text: para });
    }
  }

  return (
    <>
      {blocks.map((block, i) =>
        block.type === "p" ? (
          <p key={`p-${i}`} style={PARAGRAPH_STYLE}>
            {block.text}
          </p>
        ) : (
          <ul key={`ul-${i}`} style={LIST_STYLE}>
            {block.items.map((item, j) => (
              <li key={j} style={{ marginBottom: 4 }}>
                {item}
              </li>
            ))}
          </ul>
        ),
      )}
      {intro ? (
        <p style={{ ...PARAGRAPH_STYLE, marginBottom: 8 }}>{intro}</p>
      ) : null}
      {items && items.length > 0 ? (
        <ul style={LIST_STYLE}>
          {items.map((item, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {item}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/** Prose section: sentence-case Inter 700 fs-title heading + paragraphs/list. */
function ProseSection({ section }: { section: DocSection }) {
  return (
    <>
      <h3
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          color: "var(--text-primary)",
          // No letter-spacing, no uppercase — this reads as a document
          // subheading, not a modal eyebrow.
          letterSpacing: 0,
          fontSize: "var(--fs-title)",
          marginTop: 0,
          marginBottom: 8,
        }}
      >
        {section.heading}
      </h3>
      <SectionProse
        body={section.body}
        intro={section.intro}
        items={section.items}
      />
    </>
  );
}

/**
 * Callout block — tinted left-bordered card. Heading keeps the
 * uppercase eyebrow look (in accent-sky / true-blue) because the block
 * itself is visually distinct from the prose flow.
 */
function CalloutSection({ section }: { section: DocSection }) {
  return (
    <div
      style={{
        background: "rgba(var(--accent-sky-rgb), 0.08)",
        borderLeft: "3px solid var(--accent-sky)",
        borderRadius: 8,
        padding: "12px 16px",
        margin: 0,
      }}
    >
      <div
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: "var(--fs-label)",
          fontWeight: 700,
          color: "var(--accent-sky)",
          letterSpacing: 1.5,
          textTransform: "uppercase",
          marginBottom: 6,
        }}
      >
        {section.heading}
      </div>
      <SectionProse
        body={section.body}
        intro={section.intro}
        items={section.items}
      />
    </div>
  );
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(1)} ${units[i]}`;
}

/**
 * Renders an uploaded pre-reading document (a BFF blob). Fetches the bytes
 * with the caller's token, embeds a PDF inline via an object URL, and always
 * offers Download / Open-in-new-tab (the robust fallback if inline embedding
 * is blocked by CSP or the type isn't a PDF). The object URL is revoked on
 * close to avoid leaking blobs.
 */
function UploadedDocView({ upload, open }: { upload: DocUpload; open: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const pdf = isPdfUpload(upload);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    setStatus("loading");
    setUrl(null);
    downloadDoc("workshops", upload.path)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, upload.path]);

  const onDownload = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = upload.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" style={{ padding: "16px 20px" }}>
      <div className="flex items-center gap-2">
        <FileText
          aria-hidden
          style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }}
        />
        <div className="min-w-0 flex-1">
          <p
            className="truncate"
            style={{ color: "var(--text-primary)", fontSize: "var(--fs-base)" }}
          >
            {upload.filename}
          </p>
          {upload.size !== undefined && (
            <p style={{ color: "var(--text-muted)", fontSize: "var(--fs-label)" }}>
              {humanSize(upload.size)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDownload}
          disabled={!url}
          className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 font-mono uppercase transition-colors disabled:opacity-40"
          style={{
            borderColor: "rgba(var(--accent-true-blue-rgb), 0.3)",
            color: "var(--text-secondary)",
            fontSize: "var(--fs-label)",
          }}
        >
          <Download aria-hidden style={{ width: 12, height: 12 }} />
          Download
        </button>
      </div>

      {status === "loading" && (
        <div
          className="flex items-center gap-2"
          style={{ color: "var(--text-muted)", fontSize: "var(--fs-base)" }}
        >
          <Loader2 aria-hidden className="animate-spin" style={{ width: 14, height: 14 }} />
          Loading document…
        </div>
      )}
      {status === "error" && (
        <p style={{ color: "var(--accent-red)", fontSize: "var(--fs-base)" }}>
          Couldn’t load the document. Try Download.
        </p>
      )}
      {status === "ready" && url && pdf && (
        <iframe
          title={upload.filename}
          src={url}
          style={{
            flex: 1,
            width: "100%",
            minHeight: "55vh",
            border: 0,
            borderRadius: 8,
            background: "#fff",
          }}
        />
      )}
      {status === "ready" && url && !pdf && (
        <div
          className="rounded-lg border p-4"
          style={{
            borderColor: "rgba(var(--accent-true-blue-rgb), 0.15)",
            color: "var(--text-secondary)",
            fontSize: "var(--fs-base)",
          }}
        >
          Inline preview isn’t available for this file type. Use Download to open it.
        </div>
      )}
    </div>
  );
}

export function DocModal({ open, onOpenChange, workshopId }: DocModalProps) {
  // Power-user override wins; falls back to the authored dossier when unset.
  // An uploaded document (if any) takes precedence over the authored sections.
  const override = useEffectiveWorkshopOverride(workshopId);
  const docUpload = resolveDocUpload(override);
  const doc = resolveField(override, "doc", DOC_BY_WORKSHOP[workshopId]);
  const workshop = WORKSHOPS.find((w) => w.id === workshopId);
  const sectionCount = doc.sections.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none", width: "72vw", maxWidth: 820 }}
      >
        <DialogTitle className="sr-only">
          Pre-reading dossier — {workshopId}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Background material to review before the {workshopId} session.
        </DialogDescription>
        <div
          className="modal-shell"
          style={{
            width: "100%",
            // Legacy: `max-height:calc(100vh - 56px - 112px - 24px)`
            // (top chrome + bottom nav + 24px gutter).
            maxHeight: "calc(var(--os-vh) * 100 - 56px - 112px - 24px)",
          }}
        >
          {/* HEADER — legacy `_legacy/wd.html` lines 115-122. The Doc
              modal's header right is the close button alone (no status
              cluster like Map/Report). */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Eye
                aria-hidden
                style={{ width: 14, height: 14, color: "var(--accent-true-blue)" }}
              />
              <span className="modal-title">PRE-READING DOSSIER</span>
              {/* Legacy DOC pill — `_legacy/wd.html` line 119. */}
              <span
                style={{
                  padding: "1px 8px",
                  background: "rgba(var(--accent-true-blue-rgb), 0.12)",
                  border: "1px solid var(--accent-true-blue)",
                  borderRadius: 10,
                  fontSize: "var(--fs-label)",
                  color: "var(--accent-true-blue)",
                  fontWeight: 600,
                  letterSpacing: 1,
                }}
              >
                {docUpload ? "DOCUMENT" : `DOC 1 OF ${sectionCount}`}
              </span>
            </div>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="modal-close-btn"
              aria-label="Close"
            >
              <X aria-hidden style={{ width: 12, height: 12 }} />
              CLOSE
            </button>
          </div>

          {/* BODY — scrolls inside the shell so long dossiers don't
              blow out the modal height. The outer `.modal-body` provides
              the scroll container; we pad it via the inner wrapper so
              the true-blue thin scrollbar styling from `.modal-body`
              applies. */}
          <div className="modal-body">
            {docUpload ? (
              <UploadedDocView upload={docUpload} open={open} />
            ) : (
            <div style={{ padding: "16px 20px" }}>
              {doc.sections.map((section, idx) => {
                const isCallout = section.kind === "callout";
                // First section: no top margin. Subsequent prose
                // sections get a subtle true-blue separator so sections
                // don't run together. Callouts skip the separator —
                // the block's own border-left/tinted background already
                // gives sufficient visual division.
                const wrapperStyle: React.CSSProperties =
                  idx === 0
                    ? {}
                    : isCallout
                      ? { marginTop: 20 }
                      : {
                          borderTop:
                            "1px solid rgba(var(--accent-true-blue-rgb), 0.08)",
                          paddingTop: 14,
                          marginTop: 20,
                        };
                return (
                  <section key={section.heading} style={wrapperStyle}>
                    {isCallout ? (
                      <CalloutSection section={section} />
                    ) : (
                      <ProseSection section={section} />
                    )}
                  </section>
                );
              })}
            </div>
            )}
          </div>

          {/* FOOTER — legacy `_legacy/wd.html` lines 128-132:
              "XAES Phase Zero | <workshop longTitle>" pair. */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span
              className="modal-footer-ws"
              style={{ color: workshopLabelColor(workshopId) }}
            >
              {workshop?.longTitle ?? `${workshopId} – Workshop`}
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}
